import { ChartActions, TempActions } from '@mode/charts/domain';
import { TableEncodingHelpers } from '@mode/charts/util-vegas';
import { ChartDesigner, VizDesignerRoot, VizDesignerState } from '@mode/legacy/types-app-state';
import { DEFAULT_FLAMINGO_PAGE_SIZE, FLAMINGO_RESERVED_REFS } from '@mode/shared/util-js';
import { combineAllReducers, ReducerTree } from '@mode/legacy/util-domain';
import { Actions } from '../actions';
import { ChartDesignerActionType } from '../actions/chart-designer/designer.actions';
import { ExplorerActionType } from '../actions/chart-designer/explorer.actions';

export const initialVizDesignerState: VizDesignerRoot = {
  vizDesigner: {
    tablePaging: {},
    tableColumnSearch: {},
    activeTableColumn: '',
    panes: {
      chartPane: {
        view: ChartDesigner.PaneView.Maximized,
        height: 70,
      },
      dataPane: {
        view: ChartDesigner.PaneView.Regular,
        height: 30,
      },
    },
    // For Pivot table quick charts
    tablePanes: {
      chartPane: {
        view: ChartDesigner.PaneView.Maximized,
        height: 100,
      },
      dataPane: {
        view: ChartDesigner.PaneView.Minimized,
        height: 0,
      },
    },
  },
};

export function tablePagingReducer(
  state = initialVizDesignerState.vizDesigner.tablePaging,
  action: Actions
): VizDesignerState['tablePaging'] {
  switch (action.type) {
    // TODO: Respond to more cleanup actions like destroy query
    case ChartActions.destroyChartSuccess.type:
      const { [action.payload]: _, ...nextPaging } = state; // destructure to remove a key
      return nextPaging;
    case ChartActions.updateType.type:
      return {
        ...state,
        [action.token]: {
          offset: 0,
          pageSize: 100,
        },
      };
    case ChartDesignerActionType.SetPagingOffset: {
      return {
        [action.vizToken]: {
          ...state[action.vizToken],
          offset: action.offset,
        },
      };
    }
    case ChartDesignerActionType.SetPageSize: {
      return {
        [action.vizToken]: {
          ...state[action.vizToken],
          pageSize: action.pageSize,
          offset: 0,
        },
      };
    }
    case ChartActions.selectChart.type:
      return {
        ...state,
        [action.token]: state[action.token] || {
          offset: 0,
          pageSize: 100,
        },
      };
    case TempActions.loadExploration.type: // fallthrough
      return {
        ...state,
        [FLAMINGO_RESERVED_REFS.Exploration]: state[FLAMINGO_RESERVED_REFS.Exploration] || {
          offset: 0,
          pageSize: action.explorationViz.format?.flatTable?.general?.pageSize ?? DEFAULT_FLAMINGO_PAGE_SIZE,
        },
      };
    case ExplorerActionType.ExploreChartOnLoad:
      return {
        ...state,
        [FLAMINGO_RESERVED_REFS.ChartExplorer]: {
          offset: 0,
          pageSize: 100,
        },
      };
    case ExplorerActionType.SetActivelyExploredPagingOffsetAndPageSize:
      return {
        ...state,
        [FLAMINGO_RESERVED_REFS.ChartExplorer]: {
          offset: action.pagingOffset,
          pageSize: action.pageSize,
        },
      };
    // case SharedChartCommandActionType.ViewChartData:
    case ExplorerActionType.ViewChartData:
    case TempActions.Type.ExploreChartWithData:
      return {
        ...state,
        [FLAMINGO_RESERVED_REFS.ViewData]: {
          offset: 0,
          pageSize: 100,
        },
      };
    case ChartActions.updateEncoding.type:
      return {
        ...state,
        [action.token]: {
          ...state[action.token],
          offset: 0,
        },
      };
  }

  return state;
}

export function tableColumnSearchReducer(
  state = initialVizDesignerState.vizDesigner.tableColumnSearch,
  action: Actions
): VizDesignerState['tableColumnSearch'] {
  switch (action.type) {
    case ChartDesignerActionType.SetTableColumnSearch:
      const { token, columnSearchModel } = action;
      return {
        [token]: columnSearchModel,
      };
  }

  return state;
}

export function activeTableColumnReducer(
  state = initialVizDesignerState.vizDesigner.activeTableColumn,
  action: Actions
): VizDesignerState['activeTableColumn'] {
  switch (action.type) {
    case ChartDesignerActionType.SetSelectedColumn:
      return action.column;
    case ChartActions.updateEncoding.type:
      if (state && !TableEncodingHelpers.hasField(action.encoding, state)) {
        return '';
      }
      break;
    case ExplorerActionType.UpdateEncoding:
      if (state && !TableEncodingHelpers.hasField(action.result.encoding, state)) {
        return '';
      }
      break;
  }

  return state;
}

export function panesReducer(
  state = initialVizDesignerState.vizDesigner.panes,
  action: Actions
): VizDesignerState['panes'] {
  switch (action.type) {
    case ChartDesignerActionType.MinimizeChartPane: // fallthrough
    case ChartDesignerActionType.MaximizeDataPane: // fallthrough
    case ExplorerActionType.MinimizeChartPane: // fallthrough
    case ExplorerActionType.MaximizeDataPane:
      return {
        ...state,
        chartPane: {
          ...state.chartPane,
          view:
            state.chartPane.view === ChartDesigner.PaneView.Minimized
              ? ChartDesigner.PaneView.Regular
              : ChartDesigner.PaneView.Minimized,
        },
        dataPane: {
          ...state.dataPane,
          view:
            state.dataPane.view === ChartDesigner.PaneView.Maximized
              ? ChartDesigner.PaneView.Regular
              : ChartDesigner.PaneView.Maximized,
        },
      };
    case ChartDesignerActionType.MaximizeChartPane: // fallthrough
    case ChartDesignerActionType.MinimizeDataPane: // fallthrough
    case ExplorerActionType.MaximizeChartPane: // fallthrough
    case ExplorerActionType.MinimizeDataPane:
      return {
        ...state,
        chartPane: {
          ...state.chartPane,
          view:
            state.chartPane.view === ChartDesigner.PaneView.Maximized
              ? ChartDesigner.PaneView.Regular
              : ChartDesigner.PaneView.Maximized,
        },
        dataPane: {
          ...state.dataPane,
          view:
            state.dataPane.view === ChartDesigner.PaneView.Minimized
              ? ChartDesigner.PaneView.Regular
              : ChartDesigner.PaneView.Minimized,
        },
      };
    default:
      return state;
  }
}

export function tablePanesReducer(
  state = initialVizDesignerState.vizDesigner.tablePanes,
  action: Actions
): VizDesignerState['tablePanes'] {
  switch (action.type) {
    case ChartDesignerActionType.MaximizeTableDataPane: // fallthrough
      return {
        ...state,
        chartPane: {
          ...state.chartPane,
          view:
            state.chartPane.view === ChartDesigner.PaneView.Minimized
              ? ChartDesigner.PaneView.Regular
              : ChartDesigner.PaneView.Minimized,
        },
        dataPane: {
          ...state.dataPane,
          view:
            state.dataPane.view === ChartDesigner.PaneView.Maximized
              ? ChartDesigner.PaneView.Regular
              : ChartDesigner.PaneView.Maximized,
        },
      };

    case ChartDesignerActionType.MinimizeTableDataPane: // fallthrough
      return {
        ...state,
        chartPane: {
          ...state.chartPane,
          view:
            state.chartPane.view === ChartDesigner.PaneView.Maximized
              ? ChartDesigner.PaneView.Regular
              : ChartDesigner.PaneView.Maximized,
        },
        dataPane: {
          ...state.dataPane,
          view:
            state.dataPane.view === ChartDesigner.PaneView.Minimized
              ? ChartDesigner.PaneView.Regular
              : ChartDesigner.PaneView.Minimized,
        },
      };

    default:
      return state;
  }
}

export const vizDesignerReducers: ReducerTree<VizDesignerRoot, VizDesignerState, Actions> = {
  tablePaging: tablePagingReducer,
  tableColumnSearch: tableColumnSearchReducer,
  activeTableColumn: activeTableColumnReducer,
  panes: panesReducer,
  tablePanes: tablePanesReducer,
};

export const vizDesignerFeatureReducer = combineAllReducers<VizDesignerRoot, keyof VizDesignerRoot, Actions>({
  vizDesigner: vizDesignerReducers,
});
